import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarDays } from "lucide-react";
import { cn } from "@/lib/utils";

const isoToDisplay = (iso) => {
  if (!iso || String(iso).length < 10) return "";
  const s = String(iso).slice(0, 10);
  return `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}`;
};
const displayToIso = (d) => {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec((d || "").trim());
  if (!m) return "";
  const [, dd, mm, yyyy] = m;
  return `${yyyy}-${mm}-${dd}`;
};

export function DateInput({ value, onChange, className, "data-testid": testId, ...props }) {
  const [text, setText] = useState(isoToDisplay(value));
  const [open, setOpen] = useState(false);
  useEffect(() => { setText(isoToDisplay(value)); }, [value]);

  const handleText = (e) => {
    const raw = e.target.value.replace(/[^\d]/g, "").slice(0, 8);
    let out = raw;
    if (raw.length > 4) out = `${raw.slice(0, 2)}/${raw.slice(2, 4)}/${raw.slice(4)}`;
    else if (raw.length > 2) out = `${raw.slice(0, 2)}/${raw.slice(2)}`;
    setText(out);
    const iso = displayToIso(out);
    if (iso) onChange(iso);
    else if (out === "") onChange("");
  };

  const selected = value && String(value).length >= 10
    ? new Date(String(value).slice(0, 10) + "T00:00:00") : undefined;

  return (
    <div className="relative">
      <Input
        {...props}
        data-testid={testId}
        value={text}
        onChange={handleText}
        placeholder="dd/mm/yyyy"
        inputMode="numeric"
        className={cn(className, "pr-9")}
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="Open calendar"
            data-testid={testId ? `${testId}-calendar` : undefined}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-emerald-400"
          >
            <CalendarDays className="w-4 h-4" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 bg-[#111827] border-[#374151] text-white" align="end">
          <Calendar
            mode="single"
            selected={selected}
            defaultMonth={selected}
            onSelect={(d) => {
              if (d) {
                const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
                onChange(iso);
                setText(isoToDisplay(iso));
              }
              setOpen(false);
            }}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
